import { useTranslation } from 'next-i18next';
import { loadDomAnimation, Tag, Text } from '@bridebook/aura';
import { GetRsvpDataResponse } from '@bridebook/web/pages/api/for/website/[websiteId]/rsvp/guest/get';
import { getGuestRsvpStatus, sortRelatedGuests } from '@bridebook/web/app-basic/lib/rsvp/utils';
import { LazyMotion, m } from 'motion/react';

const STAGGER_DELAY = 0.15;
interface PartyListProps {
  relatedGuests: GetRsvpDataResponse['relatedGuests'];
  onGuestClick?: (member: GetRsvpDataResponse['relatedGuests'][number]) => void;
}

export const PartyList = ({ relatedGuests, onGuestClick }: PartyListProps) => {
  const { t } = useTranslation('weddingWebsite');

  const [currentGuest, ...rest] = relatedGuests;
  const sortedGuests = [currentGuest, ...sortRelatedGuests(rest)];
  const allResponded = relatedGuests.every(
    (member) => getGuestRsvpStatus(member.invitation) === 'responded',
  );

  if (relatedGuests.length <= 0) return null;

  return (
    <LazyMotion features={loadDomAnimation}>
      <m.div
        className="flex flex-col"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.25 }}>
        <m.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.35, ease: 'easeInOut' }}>
          <Text variant="typo-xs" className="py-4 text-center text-space">
            {allResponded
              ? t('rsvp.guest.name.partyInfo_allResponded')
              : t('rsvp.guest.name.partyInfo')}
          </Text>
        </m.div>
        <ul className="flex flex-col gap-3">
          {sortedGuests.map((member, index) => {
            const status = getGuestRsvpStatus(member.invitation);
            return (
              <m.li
                key={member.id}
                className="flex items-center justify-between"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.3,
                  ease: 'easeOut',
                  delay: 0.3 + index * STAGGER_DELAY,
                }}>
                <Text
                  variant="typo-sm-semibold"
                  {...(index !== 0 && onGuestClick
                    ? {
                        as: 'button',
                        onClick: () => onGuestClick(member),
                        className: 'cursor-pointer',
                      }
                    : {})}>
                  {member.name}
                  {index === 0 && (
                    <Text as="span" variant="typo-sm">
                      {' '}
                      {`(${t('rsvp.guest.name.partyYou')})`}
                    </Text>
                  )}
                </Text>
                {status === 'noInvitation' ? (
                  <Tag theme="space08">{t('rsvp.guest.name.partyInvitationPending')}</Tag>
                ) : status === 'responded' ? (
                  <Tag theme="teal80">{t('rsvp.guest.name.partyResponded')}</Tag>
                ) : (
                  <Tag theme="indigoCrush10">{t('rsvp.guest.name.partyNotResponded')}</Tag>
                )}
              </m.li>
            );
          })}
        </ul>
      </m.div>
    </LazyMotion>
  );
};
